import { setInteractionError } from '@atlassian/react-ufo/set-interaction-error';
import { UPGRADE_SUBMIT_CHANGELIST_UFO_NAME } from 'util/ufoNameConstants';

import appconfig from 'appconfig';
import defaultMessages from 'modules/i18n/messages.json';

export const getMessage = (id: string) => defaultMessages[id] || '';

export const RESTRICTED_EMAIL_DOMAINS = ['ru', 'by', 'рф', 'рус', 'бел'];
export const RESTRICTED_COUNTRIES_ISO_CODES = ['RU', 'BY'];

export const setInteractionErrorUpgradeSubmitChangelist = () => {
  setInteractionError(UPGRADE_SUBMIT_CHANGELIST_UFO_NAME, {
    errorMessage: 'upgrade DC add items error',
    name: 'upgrade DC add items error',
  });
};

const countryIsoCodeChile = 'CL';
const countryIsoCodePhilippines = 'PH';
const countryIsoCodeEgypt = 'EG';
const countryIsoCodeThailand = 'TH';
const countryIsoCodeCanada = 'CA';
const countryIsoCodeSerbia = 'RS';
const countryIsoCodeUkraine = 'UA';
const countryIsoCodeUzbekistan = 'UZ';
const taxExemptionTypes = ['EG_UIN', 'CA_GST_HST', 'MB_RST', 'QST', 'CA_SK_PST'];

export const checkIsCountryChile = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodeChile;

export const checkIsCountryPhilippines = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodePhilippines;

export const checkIsCountryCanada = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodeCanada;

export const checkIsCountryEgypt = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodeEgypt;

export const checkIsCountryThailand = (isoCountryCode: string = '') => {
  return isoCountryCode === countryIsoCodeThailand;
};

export const checkIsCountrySerbia = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodeSerbia;

export const checkIsCountryUkraine = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodeUkraine;

export const checkIsCountryUzbekistan = (isoCountryCode: string = '') =>
  isoCountryCode === countryIsoCodeUzbekistan;

export const checkIsAllowedTaxExemptionType = (type: string = '') =>
  taxExemptionTypes.includes(type);

export const getVATResponsibleCopies = (isoCountryCode: string | undefined) => {
  let taxIdPlaceholder: string;
  let VATRequiredMessageId: string;
  let VATNotRequiredMessageId: string;

  switch (isoCountryCode) {
    case countryIsoCodeChile: {
      taxIdPlaceholder = 'XX.XXX.XXX-X';
      VATRequiredMessageId = 'checkout.contact.tax-id-vat-required';
      VATNotRequiredMessageId = 'checkout.contact.tax-id-vat-not-required';
      break;
    }
    case countryIsoCodePhilippines: {
      taxIdPlaceholder = 'XXX-XXX-XXX or XXX-XXX-XXX-XXX';
      VATRequiredMessageId = 'checkout.contact.tax-id-vat-required.philippines';
      VATNotRequiredMessageId = 'checkout.contact.tax-id-vat-not-required.philippines';
      break;
    }
    case countryIsoCodeThailand: {
      taxIdPlaceholder = 'XXXXXXXXXXXXX';
      VATRequiredMessageId = 'checkout.contact.tax-id-vat-required.thailand';
      VATNotRequiredMessageId = 'checkout.contact.tax-id-vat-not-required.thailand';
      break;
    }
    case countryIsoCodeSerbia: {
      taxIdPlaceholder = 'XXXXXXXXXXXXX';
      VATRequiredMessageId = 'checkout.contact.tax-id-vat-required.serbia';
      VATNotRequiredMessageId = 'checkout.contact.tax-id-vat-not-required.serbia';
      break;
    }
    case countryIsoCodeUkraine: {
      taxIdPlaceholder = 'XXXXXXXX or XXXXXXXXXXXX';
      VATRequiredMessageId = 'checkout.contact.tax-id-vat-required.ukraine';
      VATNotRequiredMessageId = 'checkout.contact.tax-id-vat-not-required.ukraine';
      break;
    }
    case countryIsoCodeUzbekistan: {
      taxIdPlaceholder = '';
      VATRequiredMessageId = 'checkout.contact.tax-id-vat-required.uzbekistan';
      VATNotRequiredMessageId = 'checkout.contact.tax-id-vat-not-required.uzbekistan';
      break;
    }
    default: {
      taxIdPlaceholder = '';
      VATRequiredMessageId = '';
      VATNotRequiredMessageId = '';
    }
  }

  return {
    isCountryChile: checkIsCountryChile(isoCountryCode),
    isCountryPhilippines: checkIsCountryPhilippines(isoCountryCode),
    isCountryThailand: checkIsCountryThailand(isoCountryCode),
    isCountrySerbia: checkIsCountrySerbia(isoCountryCode),
    isCountryUkraine: checkIsCountryUkraine(isoCountryCode),
    isCountryUzbekistan: checkIsCountryUzbekistan(isoCountryCode),
    taxIdPlaceholder,
    VATRequiredMessageId,
    VATNotRequiredMessageId,
  };
};

export const getTaxExemptionNoByTaxType = (taxExemptions, type) => {
  if (typeof taxExemptions === 'string') {
    return taxExemptions;
  }
  return taxExemptions?.find(({ taxType }) => type === taxType)?.exemptionNo || '';
};

export const getTaxExemptionNoByCountryState = (isoCountryCode, taxId, state, taxExemptions) => {
  const isCountryEgypt = checkIsCountryEgypt(isoCountryCode);
  const isCountryCanada = checkIsCountryCanada(isoCountryCode);

  if (isCountryEgypt && taxId) {
    return getTaxExemptionNoByTaxType(taxExemptions, 'EG_UIN');
  }
  if (isCountryCanada && (state === 'BC' || state === 'QC' || state === 'SK')) {
    return getTaxExemptionNoByTaxType(taxExemptions, 'CA_GST_HST');
  }
  if (isCountryCanada && state === 'MB') {
    return getTaxExemptionNoByTaxType(taxExemptions, 'MB_RST');
  }
  return '';
};

export const getTaxExemptions = (
  isoCountryCode: any,
  taxId: any,
  state: any,
  taxExemptions: any,
) => {
  const isCountryEgypt = checkIsCountryEgypt(isoCountryCode);
  const isCountryCanada = checkIsCountryCanada(isoCountryCode);

  if (isCountryEgypt && taxId) {
    return [
      { exemptionNo: taxId, taxType: 'EG_VAT' },
      { exemptionNo: taxExemptions, taxType: 'EG_UIN' },
    ];
  }
  if (isCountryCanada && state === 'MB') {
    return [
      { exemptionNo: taxId, taxType: 'CA_GST_HST' },
      { exemptionNo: taxExemptions, taxType: 'MB_RST' },
    ];
  }
  if (isCountryCanada && state === 'BC') {
    return [
      { exemptionNo: taxId, taxType: 'CA_BC_PST' },
      { exemptionNo: taxExemptions, taxType: 'CA_GST_HST' },
    ];
  }
  if (isCountryCanada && state === 'QC') {
    return [
      { exemptionNo: taxId, taxType: 'QST' },
      { exemptionNo: taxExemptions, taxType: 'CA_GST_HST' },
    ];
  }
  if (isCountryCanada && state === 'SK') {
    return [
      { exemptionNo: taxId, taxType: 'CA_SK_PST' },
      { exemptionNo: taxExemptions, taxType: 'CA_GST_HST' },
    ];
  }
  return [];
};

export const taxExemptionsLabels = {
  Egypt: 'UIN',
  Canada: {
    MB: 'RST',
    BC: 'GST/HST',
    SK: 'GST/HST',
    QC: 'GST/HST',
  },
};

export const isSen = (value: string): boolean => {
  const SEN_REGEXP = /^(SEN-)?\d+$/;
  return SEN_REGEXP.test(value);
};

export const isSlug = (value: string): boolean => {
  const SLUG_REGEXP = /^E-(?:[A-Z0-9]+-){3}[A-Z0-9]+$/;
  return SLUG_REGEXP.test(value);
};

export const getComparatorUrl = (): string =>
  `${appconfig.wacUrl}/migration/assess/calculate-pricing`;
